
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo';
import { IModule } from '@/model/Community/communityInterfaces';
import { COMMUNITY_VERTICALS } from '@/model/Community/communityConstants';
import { IErrors } from '@/model/IErrors';
import { IAuthUserData } from '@/store/modules/authModule';
import { EUserPermissions } from '@/model/EUserPermissions';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { IShortChannel } from '@/model/shortInterfaces';

const communityModule = namespace('communityModule');
const authModule = namespace('authModule');
const channelModule = namespace('channelModule');

@Component({
  name: 'CommunityCreate',
  components: { BoPanel },
})
export default class CommunityCreate extends Vue {
  @communityModule.Getter('errors')
  protected errors!: IErrors;

  @communityModule.Getter('countries')
  protected countries!: { id: number; name: string }[];

  @communityModule.Action('createCommunity')
  private createCommunity!: (data: any) => Promise<any>;

  @communityModule.Getter('availableModules')
  protected availableModules!: IModule[];

  @communityModule.Action('fetchCountries')
  private fetchCountries!: () => Promise<any>;

  @communityModule.Action('fetchModules')
  private fetchModules!: () => Promise<any>;

  @channelModule.Getter('channels')
  private channels!: IShortChannel[];

  @channelModule.Action('fetchChannels')
  private fetchChannels!: () => void;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  protected loading: boolean = false;
  protected saving: boolean = false;
  protected moduleIdsError: string | null = null;
  private subscribes: IOnOffHandlers = {};

  private form: any = {
    name: '',
    slug: '',
    channel_id: null,
    status: 'active',
    languages: ['es-ES'],
    currency: 'EUR',
    vertical: null,
    location_name: '',
    location_line_1: '',
    location_line_2: '',
    location_city: '',
    location_postcode: '',
    location_country_id: null,
    coordinates_lat: null,
    coordinates_long: null,
    module_ids: [],
  };

  get isDeveloper(): boolean {
    return PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_DEVELOPER);
  }

  get channelOptions(): IShortChannel[] {
    return this.channels || [];
  }

  get statusOptions() {
    return [
      { text: this.$t('communities.status.active'), value: 'active' },
      { text: this.$t('communities.status.inactive'), value: 'inactive' },
    ];
  }

  get currencyOptions() {
    return [
      { text: 'EUR', value: 'EUR' },
    ];
  }

  get verticalOptions() {
    return COMMUNITY_VERTICALS.map((id) => ({
      text: this.$t('lockers.locker.fields.vertical.values.' + id),
      value: id,
    }));
  }

  get availableLanguages() {
    return ['es-ES', 'ca-ES', 'en-GB', 'fr-FR', 'pt-PT', 'dk-DK', 'de-DE'];
  }

  toggleModule(moduleId: number) {
    const idx = this.form.module_ids.indexOf(moduleId);
    if (idx === -1) {
      this.form.module_ids = [...this.form.module_ids, moduleId];
    } else {
      this.form.module_ids = this.form.module_ids.filter((id: number) => id !== moduleId);
    }
    if (this.form.module_ids.length > 0) {
      this.moduleIdsError = null;
    }
  }

  private rules = {
    required: (v: any) => !!v || this.$t('common.validation.required'),
    requiredArray: (v: any[]) => (v && v.length > 0) || this.$t('common.validation.required'),
    min2: (v: string) => (v && v.length >= 2) || this.$t('common.validation.min', { min: 2 }),
  };

  created() {
    this.subscribes = this.$ons({
      'backend.call.begin.communityCreated': () => { this.saving = true; },
      'backend.call.OK.communityCreated': this.onCreateSuccess,
      'backend.call.KO.communityCreated': () => { this.saving = false; },
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.fetchCountries();
    this.fetchChannels();
    this.fetchModules();
  }

  private onSave() {
    const formRef: any = this.$refs.createForm;
    const formValid = formRef ? formRef.validate() : true;
    this.moduleIdsError = this.form.module_ids.length === 0
      ? this.$t('common.validation.required') as string
      : null;
    if (!formValid || this.moduleIdsError) return;
    this.createCommunity({ ...this.form });
  }

  private onCreateSuccess(community: any) {
    this.saving = false;
    this.$uiBus.emit('success', this.$t('common.saved'));
    if (community && community.id) {
      this.$router.push({ name: 'community', params: { communityId: '' + community.id } });
    } else {
      this.$router.push({ name: 'communityList' });
    }
  }

  private onCancel() {
    this.$router.push({ name: 'communityList' });
  }
}
